type PayloadMenuActionLike = {
    label: string
}

type BsvAttachmentLike = {
    bsv_tx?: Record<string, unknown> | null
}

type IdLike = string | number

type PayloadOrdinalLike = {
    id?: IdLike | null
    quest_id?: IdLike | null
    answer_id?: IdLike | null
    system_thread_id?: IdLike | null
    system_thread_answer_id?: IdLike | null
}

export type PayloadOrdinalIndicator = {
    kind: 'ordinal'
    ordinalId: string
    questId?: string | null
    answerId?: string | null
}

export type PayloadOrdinalThreadTarget = {
    questId: string
    answerId: string
}

type EnsureOrdinalThreadResult = {
    child_quest_id?: IdLike | null
    answer_id?: IdLike | null
}

const CREATION_ACTION_LABELS = new Set(['edit', 'animate', 'extend', 'remix', 'gif'])
const CHAIN_ACTION_LABELS = new Set(['ordinal', 'ordinal thread', 'onchain', 'blockchain'])

function normalizedActionLabel(action: PayloadMenuActionLike): string {
    return action.label.trim().toLowerCase()
}

function normalizeChainAction<T extends PayloadMenuActionLike>(action: T): T {
    const label = normalizedActionLabel(action)
    if (label !== 'onchain' && label !== 'blockchain') return action
    return { ...action, label: 'Onchain' }
}

export function payloadChainActionLabel(kind?: 'ordinal' | 'onchain' | null): string {
    return kind === 'ordinal' ? 'Ordinal' : 'Onchain'
}

export function payloadOrdinalThreadActionLabel(): string {
    return 'Ordinal Thread'
}

export function payloadOrdinalIndicator(
    ordinal?: PayloadOrdinalLike | null,
): PayloadOrdinalIndicator | null {
    if (!ordinal?.id) return null

    const questId = ordinal.system_thread_id || ordinal.quest_id || null
    const answerId = ordinal.system_thread_answer_id || ordinal.answer_id || null
    return {
        kind: 'ordinal',
        ordinalId: String(ordinal.id),
        questId: questId ? String(questId) : null,
        answerId: answerId ? String(answerId) : null,
    }
}

export async function resolvePayloadOrdinalThreadTarget(
    indicator: PayloadOrdinalIndicator,
    ensureOrdinalThread: (ordinalId: string) => Promise<EnsureOrdinalThreadResult>,
): Promise<PayloadOrdinalThreadTarget | null> {
    if (indicator.questId && indicator.answerId) {
        return { questId: indicator.questId, answerId: indicator.answerId }
    }

    const ensured = await ensureOrdinalThread(indicator.ordinalId)
    if (!ensured?.child_quest_id || !ensured?.answer_id) return null

    return {
        questId: String(ensured.child_quest_id),
        answerId: String(ensured.answer_id),
    }
}

export type OpenPayloadOrdinalThreadOutcome =
    // The thread was already known (frozen or live pointer) and opened directly.
    | 'opened'
    // The thread was materialized through ensure_thread, then opened.
    | 'ensured'
    // A guest needed a write to get there: the auth prompt was shown instead.
    | 'auth'
    // Nothing to open: no ordinal id, or the server returned no thread.
    | 'unavailable'
    // ensure_thread threw (network, 5xx, expired session).
    | 'failed'

export type OpenPayloadOrdinalThreadDeps = {
    isSignedIn: boolean
    ensureOrdinalThread: (ordinalId: string) => Promise<EnsureOrdinalThreadResult>
    openAuthModal: () => void
    openRight: (target: PayloadOrdinalThreadTarget) => unknown
    // 'unavailable' and 'failed' land here (with the thrown error, when any)
    // so the surface can tell the user; the payload menu and the market card
    // share the same message.
    onFailure?: (outcome: 'unavailable' | 'failed', error?: unknown) => void
}

export const ORDINAL_THREAD_UNAVAILABLE_MESSAGE =
    "Couldn't open this ordinal's thread. Please try again."

// The one way an ordinal's thread is opened from a post surface (payload menu
// "Ordinal Thread", market card): a known thread opens directly, guests
// included; an unknown one is materialized through ensure_thread, which
// writes, so guests get the auth prompt instead.
export async function openPayloadOrdinalThread(
    indicator: PayloadOrdinalIndicator | null | undefined,
    deps: OpenPayloadOrdinalThreadDeps,
): Promise<OpenPayloadOrdinalThreadOutcome> {
    if (!indicator?.ordinalId) return 'unavailable'

    const known = Boolean(indicator.questId && indicator.answerId)
    if (!known && !deps.isSignedIn) {
        deps.openAuthModal()
        return 'auth'
    }

    let target: PayloadOrdinalThreadTarget | null
    try {
        target = await resolvePayloadOrdinalThreadTarget(indicator, deps.ensureOrdinalThread)
    } catch (error) {
        deps.onFailure?.('failed', error)
        return 'failed'
    }
    if (!target) {
        deps.onFailure?.('unavailable')
        return 'unavailable'
    }

    try {
        await deps.openRight(target)
    } catch (error) {
        deps.onFailure?.('failed', error)
        return 'failed'
    }
    return known ? 'opened' : 'ensured'
}

export function payloadOnchainIndicatorForAttachment(
    attachment?: BsvAttachmentLike | null,
): { kind: 'onchain'; bsvTx: Record<string, unknown> | null } | null {
    if (!attachment) return null
    return { kind: 'onchain', bsvTx: attachment.bsv_tx || null }
}

// Owner-paid "Write Onchain" (POST bsv/attachments/write_onchain): the post's owner pays from
// their own wallet to write one media file onchain, above the platform's automatic 1MB write.
export const WRITE_ONCHAIN_FEATURE = 'pay_write_onchain'
export const WRITE_ONCHAIN_LABEL = 'Write Onchain'
export const WRITE_ONCHAIN_MAX_BYTES = 50 * 1024 * 1024
// The same per-transaction allowance the mint estimate and the server's funds check add for
// inputs, change and the MAP record.
export const WRITE_ONCHAIN_TX_OVERHEAD_BYTES = 600
// The bsv service funds at the live miner rate plus a 10% safety margin (SHUAllet's
// payForRawTx), so the row quotes what the owner will actually be charged.
export const WRITE_ONCHAIN_FEE_MARGIN = 1.1

export type PayloadWriteOnchainState = 'available' | 'writing' | 'too_large'

type PayloadBsvTxLike = { tx_id?: unknown; status?: unknown } | null | undefined

type PayloadBsvAttachmentRecordLike = {
    attachment_id?: unknown
    bsv_tx?: PayloadBsvTxLike
}

const ENDED_ONCHAIN_WRITE_STATUSES = new Set(['failed', 'cancelled'])

// The attachment transaction for exactly one media file, matched by attachment id. A converted
// video is written under its original recording's id, so a video tile passes both. (The tile's
// chain indicator can come from a looser match that, in a multi-image post, finds another
// image's record.)
export function payloadAttachmentBsvTx(
    bsvAttachments: PayloadBsvAttachmentRecordLike[] | null | undefined,
    attachmentIds: unknown[],
): PayloadBsvTxLike {
    const ids = new Set(
        attachmentIds.filter(id => id !== null && id !== undefined && id !== '').map(String),
    )
    if (ids.size === 0 || !Array.isArray(bsvAttachments)) return null

    const record = bsvAttachments.find(attachment => ids.has(String(attachment?.attachment_id)))
    return record?.bsv_tx || null
}

type PayloadRecordingIdsLike =
    | { recording_attachment_id?: unknown; mp4_recording_attachment_id?: unknown }
    | null
    | undefined

const isSameAttachmentId = (left: unknown, right: unknown) =>
    left !== null &&
    left !== undefined &&
    left !== '' &&
    right !== null &&
    right !== undefined &&
    String(left) === String(right)

// Whether a video tile shows the post's own recording, as uploaded or converted to MP4, rather
// than, say, an AI-generated video in the same post.
export function isPayloadOwnRecording(
    answer: PayloadRecordingIdsLike,
    attachmentId: unknown,
): boolean {
    return (
        isSameAttachmentId(attachmentId, answer?.recording_attachment_id) ||
        isSameAttachmentId(attachmentId, answer?.mp4_recording_attachment_id)
    )
}

// The ids a tile's attachment record can be kept under: its own, plus the original recording's
// for a converted video's MP4, which the write files under that id.
export function payloadWriteOnchainAttachmentIds(
    answer: PayloadRecordingIdsLike,
    attachmentId: unknown,
): unknown[] {
    return isSameAttachmentId(attachmentId, answer?.mp4_recording_attachment_id)
        ? [attachmentId, answer?.recording_attachment_id]
        : [attachmentId]
}

// What the owner's Write Onchain row shows for one media file, or null when it is hidden: the
// file is minted (the inscription already holds it), already written, or not the viewer's to pay
// for. A transaction without a txid that has not failed is still on its way, whichever lane
// started it.
export function payloadWriteOnchainState({
    canWrite,
    isMinted,
    bsvTx = null,
    isWriting = false,
    byteSize = null,
}: {
    canWrite: boolean
    isMinted: boolean
    bsvTx?: PayloadBsvTxLike
    isWriting?: boolean
    byteSize?: number | null
}): PayloadWriteOnchainState | null {
    if (!canWrite || isMinted) return null
    if (bsvTx?.tx_id) return null

    const status = typeof bsvTx?.status === 'string' ? bsvTx.status : null
    if (isWriting || (status && !ENDED_ONCHAIN_WRITE_STATUSES.has(status))) return 'writing'
    if (typeof byteSize === 'number' && byteSize > WRITE_ONCHAIN_MAX_BYTES) return 'too_large'
    return 'available'
}

export function payloadWriteOnchainSats(
    byteSize: number | null | undefined,
    feeRateSatsPerByte: number | null | undefined,
): number | null {
    const bytes = Number(byteSize)
    const rate = Number(feeRateSatsPerByte)
    if (!Number.isFinite(bytes) || bytes <= 0 || !Number.isFinite(rate) || rate <= 0) return null

    const sats = (bytes + WRITE_ONCHAIN_TX_OVERHEAD_BYTES) * rate * WRITE_ONCHAIN_FEE_MARGIN
    // Round away float noise first (0.1 * 1.1 is 0.11000000000000001) so a whole-sat fee is
    // not rounded up by one.
    return Math.ceil(Math.round(sats * 1e6) / 1e6)
}

export function formatPayloadSats(sats: number): string {
    return `${Math.round(sats).toLocaleString('en-US')} sats`
}

export function isPayloadMediaMinted({
    kind,
    chainKind,
    hasAnswerOrdinal = false,
    hasImageOrdinal = false,
    hasVideoOrdinal = false,
}: {
    kind?: string | null
    chainKind?: string | null
    hasAnswerOrdinal?: boolean
    hasImageOrdinal?: boolean
    hasVideoOrdinal?: boolean
}): boolean {
    return Boolean(
        chainKind === 'ordinal' ||
            (kind === 'image' && hasImageOrdinal) ||
            (kind === 'video' && hasVideoOrdinal) ||
            // has_ordinal is a legacy answer-wide fallback for image/video attachments. It
            // cannot identify a specific audio file in a combined card.
            (kind !== 'audio' && hasAnswerOrdinal),
    )
}

export function groupPayloadMenuActions<T extends PayloadMenuActionLike>(
    actions: T[],
): {
    chainActions: T[]
    utilityActions: T[]
    creationActions: T[]
} {
    const chainActions: T[] = []
    const utilityActions: T[] = []
    const creationActions: T[] = []

    actions.forEach(action => {
        const label = normalizedActionLabel(action)
        if (CHAIN_ACTION_LABELS.has(label)) {
            chainActions.push(normalizeChainAction(action))
        } else if (CREATION_ACTION_LABELS.has(label)) {
            creationActions.push(action)
        } else {
            utilityActions.push(action)
        }
    })

    return { chainActions, utilityActions, creationActions }
}

export function shouldAddPayloadPlaylistMenuAction({
    hasMedia,
    playlistMediaKey,
    mediaKind,
    actions,
}: {
    hasMedia: boolean
    playlistMediaKey?: string | null
    mediaKind?: string | null
    actions: PayloadMenuActionLike[]
}): boolean {
    const supportsPlaylist = mediaKind === 'audio' || mediaKind === 'video'
    const hasPlaylistAction = actions.some(action =>
        ['add to playlist', 'remove from playlist'].includes(normalizedActionLabel(action)),
    )

    return Boolean(hasMedia && playlistMediaKey && supportsPlaylist && !hasPlaylistAction)
}

// Cinema shows one picture on the app window, so only a dockable video offers it; a
// combined image+audio card or an image tile has nothing to project.
export function shouldAddPayloadCinemaMenuAction({
    hasMedia,
    playlistMediaKey,
    mediaKind,
    actions,
}: {
    hasMedia: boolean
    playlistMediaKey?: string | null
    mediaKind?: string | null
    actions: PayloadMenuActionLike[]
}): boolean {
    const hasCinemaAction = actions.some(action => normalizedActionLabel(action) === 'cinema')

    return Boolean(hasMedia && playlistMediaKey && mediaKind === 'video' && !hasCinemaAction)
}

export function shouldShowPayloadTagMenuAction({
    hasTagPreview,
    status,
}: {
    hasTagPreview: boolean
    status?: string | null
}): boolean {
    return hasTagPreview && status !== 'failed'
}

// Combined media puts two files behind one dropdown, so an action that names a file has to
// name which file. Download, Mint and the chain rows are qualified with the media kind even
// when only one side offers them — "Mint" alone in a two-file card doesn't say what it mints.
// Audio-native rows (Add to playlist, Lyrics) and image-only generation rows (Edit, Animate)
// already read unambiguously against the media that can accept them, so they stay bare.
const FILE_SCOPED_MENU_LABELS = new Set([
    'download',
    'mint',
    'retry mint',
    'ordinal',
    'ordinal thread',
    'onchain',
    'write onchain',
])

// Download acts on the file, so it takes the media kind: "Download image". Minting inscribes
// the work rather than the file, so it takes the word for the work: "Mint artwork".
const WORK_SCOPED_MENU_LABELS = new Set([
    'mint',
    'retry mint',
    'ordinal',
    'ordinal thread',
    'onchain',
])
const WORK_NOUNS: Record<string, string> = { image: 'artwork', audio: 'track', video: 'clip' }

function menuQualifier(label: string, kind: string): string {
    return (WORK_SCOPED_MENU_LABELS.has(label) && WORK_NOUNS[kind]) || kind
}

export type MediaMenuGroups<T extends PayloadMenuActionLike> = {
    kind?: string | null
    chain: T[]
    utility: T[]
    playlist: T[]
    tag: T[]
    creation: T[]
}

const MEDIA_MENU_GROUP_ORDER = ['chain', 'utility', 'playlist', 'tag', 'creation'] as const

export function combineMediaMenuGroups<T extends PayloadMenuActionLike>(
    image: MediaMenuGroups<T>,
    audio: MediaMenuGroups<T>,
): T[] {
    const labelsOf = (groups: MediaMenuGroups<T>) =>
        new Set(
            MEDIA_MENU_GROUP_ORDER.flatMap(key => groups[key]).map(action =>
                normalizedActionLabel(action),
            ),
        )
    const audioLabels = labelsOf(audio)
    const colliding = new Set([...labelsOf(image)].filter(label => audioLabels.has(label)))

    const qualify = (action: T, kind?: string | null): T => {
        const label = normalizedActionLabel(action)
        if (!kind) return action
        if (!FILE_SCOPED_MENU_LABELS.has(label) && !colliding.has(label)) return action
        const qualifier = menuQualifier(label, kind)
        // "Write image onchain" reads as a sentence; "Write Onchain image" does not.
        if (label === 'write onchain') return { ...action, label: `Write ${qualifier} onchain` }
        return { ...action, label: `${action.label} ${qualifier}` }
    }

    return MEDIA_MENU_GROUP_ORDER.flatMap(key => {
        // Within a group, an action the two media share emits both rows back to back, so
        // "Download image" is followed by "Download audio" rather than by the image's Mint.
        const audioActions = audio[key]
        const taken = new Set<T>()
        const rows: T[] = []

        image[key].forEach(action => {
            rows.push(qualify(action, image.kind))
            const match = audioActions.find(
                entry =>
                    !taken.has(entry) &&
                    normalizedActionLabel(entry) === normalizedActionLabel(action),
            )
            if (match) {
                taken.add(match)
                rows.push(qualify(match, audio.kind))
            }
        })
        audioActions.forEach(action => {
            if (!taken.has(action)) rows.push(qualify(action, audio.kind))
        })

        return rows
    })
}
